import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { PageHero } from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { siteBrand } from "@/mocks/site";
import TipForm from "./components/TipForm";

const submitSchema = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "提交线索 - 暗线独家爆料线索征集中心",
    url: "https://hongshanyang.cn/submit",
    description: "独家爆料线索在线投递表，支持匿名提交、材料说明与联系邮箱，提交不做 IP 留痕。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hongshanyang.cn/" },
      { "@type": "ListItem", position: 2, name: "提交线索", item: "https://hongshanyang.cn/submit" },
    ],
  },
];

const notices = [
  {
    icon: "ri-user-forbid-line",
    title: "不必留下真实身份",
    desc: "称呼与代号可以留空。若你希望被联系，只需一个与生活账号隔离的邮箱即可。",
  },
  {
    icon: "ri-split-cells-horizontal",
    title: "可以分批提供材料",
    desc: "先用文字说明情况，确认双方可靠之后，再决定交出多少材料、交出哪些材料。",
  },
  {
    icon: "ri-shield-check-line",
    title: "安全优先于完整",
    desc: "如果某个文件涉及同事或家人，请先说明，我们会一起判断是否有必要提供。",
  },
];

const afterSteps = [
  { step: "01", title: "进入加密队列", desc: "提交内容直接进入仅核实小组可读的队列，与你的网络身份分开存储。" },
  { step: "02", title: "72 小时内回应", desc: "若你在表中留下邮箱，核实小组会在 72 小时内与你就疑点进行第一次沟通。" },
  { step: "03", title: "交叉核验", desc: "材料会与公开信息、旁证互相印证。这个过程可能持续数天到数周，你会被告知进展。" },
  { step: "04", title: "脱敏与确认", desc: "呈现之前，我们会完成脱敏并把方案交给你确认，你有权要求删改或叫停。" },
];

export default function SubmitPage() {
  useSeo({
    title: "提交线索｜暗线独家爆料投稿与私信通道",
    description:
      "在暗线提交独家爆料线索：在线投递表支持匿名提交、材料说明与联系邮箱，提交不做 IP 留痕，72 小时内首次回应，呈现前完成脱敏并与你确认。",
    keywords: "独家爆料投稿,独家爆料私信,独家爆料线索提交,匿名爆料通道,线索征集入口",
    path: "/submit",
    schema: submitSchema,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <PageHero
          eyebrow="SUBMIT A TIP · 线索投递"
          title="提交一条线索，从一句话开始"
          description="你可以匿名、可以只写摘要、也可以先只留下一个联系方式。提交之后，核实小组会按你的要求在安全边界内处理这条线索。"
          crumbs={[{ label: "首页", to: "/" }, { label: "提交线索" }]}
          image="https://readdy.ai/api/search-image?query=dark%20moody%20close%20up%20of%20hands%20sliding%20a%20sealed%20kraft%20envelope%20across%20a%20charcoal%20desk%20toward%20a%20soft%20warm%20light%2C%20deep%20shadows%2C%20crimson%20accent%20rim%20light%2C%20fine%20film%20grain%2C%20cinematic%20confidential%20editorial%20photography%2C%20no%20faces&width=1920&height=900&seq=anxian-submit-hero&orientation=landscape"
          imageAlt="双手将密封信封推向暖光处，代表匿名提交独家爆料线索"
          meta={
            <ul className="flex flex-wrap gap-x-7 gap-y-3">
              {["不做 IP 留痕", "支持全程匿名", "72 小时内回应"].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-background-200/85">
                  <i className="ri-check-line text-accent-300"></i>
                  {item}
                </li>
              ))}
            </ul>
          }
        />

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-16">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  填表之前，先确认三件事
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">
                  填写表格只需几分钟。但如果你先把下面三点想清楚，核实的过程会顺利很多，你的暴露风险也会更低。
                </p>

                <div className="mt-8 space-y-5">
                  {notices.map((item) => (
                    <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                        <p className="mt-1.5 text-sm leading-7 text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-primary-800">
                    <i className="ri-alert-line"></i>
                    关于安全的重要提醒
                  </h3>
                  <p className="mt-2 text-xs leading-6 text-primary-900">
                    请避免使用单位设备与办公网络提交材料。我们不鼓励任何可能让你承担不可控风险的行为，安全永远优先于一条线索。
                  </p>
                </div>
              </div>

              <div>
                <TipForm />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              提交之后，会发生什么
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              我们把每一步都提前告诉你，避免你在等待中产生不确定感。
            </p>

            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {afterSteps.map((item, index) => (
                <Reveal key={item.step} delay={index * 70}>
                  <article className="h-full rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="font-heading text-2xl font-bold text-primary-300">{item.step}</span>
                    <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-foreground-950 py-16 md:py-20">
          <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-8 px-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                不方便填表？改用加密邮箱
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-background-200/80">
                你也可以只发一封邮件说明情况，我们会用同样的流程处理。邮件正文同样建议不要附带你自己的身份信息。
              </p>
            </div>
            <a
              href={`mailto:${siteBrand.email}`}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-7 py-3.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-mail-lock-line text-base"></i>
              {siteBrand.email}
            </a>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}